import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionTitle } from "@/components/AppTable";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import SmtpSettings from "./SmtpSettings";

export default function Admin() {
  const [tab, setTab] = useState("system");
  const [health, setHealth] = useState(null);
  const [scoring, setScoring] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [query, setQuery] = useState("finance");
  const [packages, setPackages] = useState("");
  const [market, setMarket] = useState("in");
  const [limit, setLimit] = useState(10);
  const [running, setRunning] = useState(false);

  const load = () => {
    api.get("/admin/health").then(r => setHealth(r.data)).catch(() => {});
    api.get("/admin/scoring").then(r => setScoring(r.data)).catch(() => {});
    api.get("/discovery/jobs").then(r => setJobs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const runDiscovery = async () => {
    setRunning(true);
    try {
      const body = { market, limit: Number(limit) };
      if (packages.trim()) body.package_names = packages.split(/[\s,]+/).filter(Boolean);
      else if (query.trim()) body.query = query;
      const { data } = await api.post("/discovery/run", body);
      toast.success(`Discovery complete: ${data.count} apps`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discovery failed");
    } finally { setRunning(false); }
  };

  const saveScoring = async () => {
    await api.put("/admin/scoring", scoring);
    toast.success("Scoring config saved");
  };

  return (
    <div className="p-8 space-y-8">
      <SectionTitle eyebrow="System" title="Admin" />

      <div className="flex gap-1 border-b border-border">
        {[
          ["system", "System & Discovery"],
          ["scoring", "Lead Scoring"],
          ["email", "Email & Templates"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "email" && <SmtpSettings />}

      {tab === "system" && <>
      <section>
        <h3 className="font-heading font-black text-lg mb-3">System Health</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 grid-borders border-l border-t border-border">
          {health && [
            ["Database", health.database],
            ["Discovered Today", health.discovered_today],
            ["Updated Today", health.updated_today],
            ["Completed Jobs", health.completed_jobs],
            ["Failed Jobs", health.failed_jobs],
            ["Last Job", health.last_job?.status || "—"],
          ].map(([l, v]) => (
            <div key={l} className="p-4">
              <div className="overline-label text-muted-foreground">{l}</div>
              <div className="font-heading font-black text-xl mt-1">{String(v)}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-heading font-black text-lg mb-3">Run Discovery</h3>
        <div className="border border-border p-4 bg-secondary/30 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input data-testid="disc-query" value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder="Search query (e.g. finance)"
                   className="border border-input bg-background px-3 py-2 text-sm rounded-sm" />
            <select data-testid="disc-market" value={market} onChange={(e) => setMarket(e.target.value)}
                    className="border border-input bg-background px-3 py-2 text-sm rounded-sm">
              <option value="in">India</option><option value="us">United States</option>
              <option value="gb">UK</option><option value="ca">Canada</option>
              <option value="au">Australia</option><option value="de">Germany</option>
              <option value="fr">France</option><option value="id">Indonesia</option>
              <option value="br">Brazil</option>
            </select>
            <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)}
                   className="border border-input bg-background px-3 py-2 text-sm rounded-sm" />
            <button data-testid="btn-run-discovery" onClick={runDiscovery} disabled={running}
                    className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium rounded-sm hover:opacity-90 disabled:opacity-50">
              {running ? "Running…" : "Run Discovery"}
            </button>
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Or specific package IDs (comma / space separated)</div>
            <input data-testid="disc-packages" value={packages} onChange={(e) => setPackages(e.target.value)}
                   placeholder="com.example.app com.another.app"
                   className="w-full border border-input bg-background px-3 py-2 text-sm font-mono rounded-sm" />
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-heading font-black text-lg mb-3">Recent Jobs</h3>
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 border-b border-border">
              <tr className="text-left overline-label text-muted-foreground">
                <th className="px-3 py-2">Started</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Query</th><th className="px-3 py-2">Market</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Results</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-border/60">
                  <td className="px-3 py-2 text-xs">{formatDate(j.started_at)}</td>
                  <td className="px-3 py-2 text-xs">{j.type}</td>
                  <td className="px-3 py-2 text-xs font-mono">{j.query || (j.package_names ? "packages" : "—")}</td>
                  <td className="px-3 py-2 text-xs">{j.market}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${j.status === "completed" ? "bg-emerald-500/10 text-emerald-700" : j.status === "failed" ? "bg-red-500/10 text-red-600" : "bg-amber-500/10 text-amber-700"}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{j.results ?? 0}</td>
                </tr>
              ))}
              {!jobs.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-sm">No jobs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      </>}

      {tab === "scoring" && scoring && (
        <section>
          <h3 className="font-heading font-black text-lg mb-3">Lead Scoring Weights</h3>
          <div className="border border-border p-4 bg-secondary/30 grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(scoring).map(([k, v]) => (
              <div key={k}>
                <div className="overline-label text-muted-foreground mb-1">{k.replace(/_/g, " ")}</div>
                <input type="number" value={v} data-testid={`score-${k}`}
                       onChange={(e) => setScoring({ ...scoring, [k]: Number(e.target.value) })}
                       className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm" />
              </div>
            ))}
            <div className="col-span-full">
              <button data-testid="btn-save-scoring" onClick={saveScoring}
                      className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm hover:opacity-90">Save</button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
